import { useSyncExternalStore } from 'react';

/**
 * "The user has their hands on a slide right now."
 *
 * Background AI turns finish whenever they finish. Two surfaces used to act on
 * that the instant it happened, regardless of what the user was doing:
 *
 *  - the auto-preview (`Canvas.jsx`) writes the proposal onto the REAL slide
 *    file, so the slide under the user's eyes silently becomes something else
 *    — and the Edit tab, which has no watcher, only finds out at the next
 *    blur-save, which 409s `preview`;
 *  - the auto-jump (`Canvas.jsx`) navigates the canvas to the first fresh
 *    proposal, which remounts EditPanel (keyed on the slide id in
 *    `CommandBar.tsx`) and destroys whatever was half-typed in it.
 *
 * Both are right for a turn the user just fired and is watching. Both are
 * wrong for one that lands three minutes later while they've moved on to
 * editing something else (Jake, 2026-07-31).
 *
 * So they ask here first, and act once the user's hands are free. The two
 * guards read the signal differently, because they disturb different things:
 *
 *  - an `edit_slide` preview only rewrites the ONE slide it targets, so it
 *    waits only while THAT slide is being edited (`isBusyOn`);
 *  - a `create_slide` preview really inserts a slide, which shifts every index
 *    below it — `useDeckEditor` clamps `activeIdx` but never re-resolves the
 *    active slide by id, so an insert above the user changes which slide they
 *    are editing with no navigation event at all. That, and the jump, wait
 *    while the user is editing ANYTHING (`isBusy`).
 *
 * Nothing is dropped while held: the proposal is already a `ready` row, so the
 * "N proposals awaiting review" bar counts it and the filmstrip tags its slide.
 * The user opts in instead of being interrupted.
 */

let busySlideId: string | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const fn of [...listeners]) fn();
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function getBusySlide(): string | null {
  return busySlideId;
}

/**
 * Focus entered (slideId) or left (null) the Edit panel. Panel-scoped, not
 * field-scoped, deliberately: tabbing from one field to the next must not read
 * as "done", or a held proposal would land in the gap between two fields.
 */
export function setBusySlide(slideId: string | null | undefined) {
  const next = slideId || null;
  if (busySlideId === next) return;
  busySlideId = next;
  emit();
}

/**
 * Let anything held through — the user asked to see it (Review / View). Focus
 * leaving the panel usually clears this on its own, but a button that jumps to
 * a slide the user is ALREADY on has no other way to release the hold, and
 * "Review" doing nothing is worse than the interruption it replaced.
 */
export function releaseBusy() {
  setBusySlide(null);
}

/** Ids carry a `base#instance` form in places; compare the manifest row. */
const baseOf = (id: string | null | undefined) => String(id || '').split('#')[0] ?? '';

/** Is the user editing this specific slide? */
export function isBusyOn(slideId: string | null | undefined): boolean {
  if (!busySlideId || !slideId) return false;
  return busySlideId === slideId || baseOf(busySlideId) === baseOf(slideId);
}

/** Is the user editing anything at all? */
export function isBusy(): boolean {
  return busySlideId !== null;
}

/**
 * The slide being edited, or null. Re-renders on change, so an effect that
 * lists it in its deps re-runs — and fires what it held — the moment the
 * user's hands come off.
 */
export function useBusySlide(): string | null {
  return useSyncExternalStore(subscribe, getBusySlide, () => null);
}
